import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DidRoutingEntry, IvrMenu, IvrOption, QueueMappingEntry, TelephonyPluginConfig } from '../../../core/telephony/telephony-plugin.config';
import { TelephonyPluginConfigService } from '../../../core/telephony/telephony-plugin-config.service';
import { TelephonyPluginManifest, TelephonyPluginManifestService } from '../../../core/telephony/telephony-plugin-manifest.service';
import { restoreCsrTheme } from '../../csr/csr-theme';

@Component({
  selector: 'app-telephony-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './telephony-settings.component.html',
  styleUrls: ['./telephony-settings.component.scss'],
})
export class TelephonySettingsComponent implements OnInit {
  config!: TelephonyPluginConfig;
  manifest: TelephonyPluginManifest | null = null;
  testPhone = '';
  testLoading = false;
  testResult: any = null;
  testError = '';
  healthResult: any = null;
  healthLoading = false;
  healthError = '';
  saveError = '';
  toast = '';

  get manifestApiEntries(): Array<{ key: string; value: string }> {
    return Object.entries(this.manifest?.api ?? {}).map(([key, value]) => ({ key, value }));
  }

  get manifestExportEntries(): Array<{ key: string; value: string }> {
    return Object.entries(this.manifest?.exports ?? {}).map(([key, value]) => ({ key, value }));
  }

  get adapterHint(): string {
    switch (this.config?.adapter.type) {
      case 'grmis':
        return 'Uses the local GRMIS complaint/citizen repositories.';
      case 'hmis':
        return 'Uses the configured URLs as HMIS webhooks for patient or appointment context.';
      case 'crm':
        return 'Uses the configured URLs as CRM webhooks for customer context.';
      case 'webhook':
        return 'Uses the configured URLs as generic business-system webhooks.';
      case 'none':
        return 'Runs telephony only, without caller lookup or external wrap-up.';
      default:
        return '';
    }
  }

  constructor(
    private readonly configService: TelephonyPluginConfigService,
    private readonly manifestService: TelephonyPluginManifestService,
  ) {}

  async ngOnInit(): Promise<void> {
    restoreCsrTheme();
    await this.configService.loadRemote();
    this.manifest = await this.manifestService.load();
    this.config = structuredClone(this.configService.value);
    await this.loadHealth();
  }

  async save(): Promise<void> {
    this.saveError = '';
    try {
      await this.configService.save(this.config);
      this.manifest = await this.manifestService.load();
      await this.loadHealth();
      this.showToast('Configuration saved');
    } catch (e) {
      this.saveError = e instanceof Error ? e.message : 'Configuration save failed';
    }
  }

  async reset(): Promise<void> {
    await this.configService.reset();
    this.config = structuredClone(this.configService.value);
    this.showToast('Configuration reset');
  }

  async testLookup(): Promise<void> {
    if (!this.testPhone.trim()) return;
    this.testLoading = true;
    this.testResult = null;
    this.testError = '';
    try {
      this.testResult = await this.configService.testLookup(this.testPhone.trim());
    } catch (e) {
      this.testError = e instanceof Error ? e.message : 'Lookup failed';
    } finally {
      this.testLoading = false;
    }
  }

  async loadHealth(): Promise<void> {
    this.healthLoading = true;
    this.healthError = '';
    try {
      this.healthResult = await this.configService.health();
    } catch (e) {
      this.healthError = e instanceof Error ? e.message : 'Health check failed';
    } finally {
      this.healthLoading = false;
    }
  }

  parseOptions(value: string): string[] {
    return value
      .split('\n')
      .map(option => option.trim())
      .filter(Boolean);
  }

  addQueue(): void {
    const entry: QueueMappingEntry = { name: '', label: '', priority: 'Medium', active: true };
    this.config.queueMapping = [...(this.config.queueMapping ?? []), entry];
  }

  removeQueue(index: number): void {
    this.config.queueMapping = this.config.queueMapping.filter((_, i) => i !== index);
  }

  addIvrMenu(): void {
    const menu: IvrMenu = {
      menuId: `menu_${Date.now()}`,
      name: '',
      greeting: '',
      options: [],
    };
    this.config.ivrMenus = [...(this.config.ivrMenus ?? []), menu];
  }

  removeIvrMenu(index: number): void {
    this.config.ivrMenus = this.config.ivrMenus.filter((_, i) => i !== index);
  }

  addIvrOption(menu: IvrMenu): void {
    const opt: IvrOption = { key: '', label: '', action: 'queue', target: '' };
    menu.options = [...(menu.options ?? []), opt];
  }

  removeIvrOption(menu: IvrMenu, optIndex: number): void {
    menu.options = menu.options.filter((_, i) => i !== optIndex);
  }

  addDidRoute(): void {
    const entry: DidRoutingEntry = { did: '', label: '', targetType: 'ivr', target: '', active: true };
    this.config.didRouting = [...(this.config.didRouting ?? []), entry];
  }

  removeDidRoute(index: number): void {
    this.config.didRouting = this.config.didRouting.filter((_, i) => i !== index);
  }

  addHoliday(): void {
    this.config.businessHours.holidays = [
      ...(this.config.businessHours.holidays ?? []),
      { date: '', name: '' },
    ];
  }

  removeHoliday(index: number): void {
    this.config.businessHours.holidays = this.config.businessHours.holidays.filter((_, i) => i !== index);
  }

  private showToast(message: string): void {
    this.toast = message;
    setTimeout(() => this.toast = '', 2500);
  }
}
